/**
 * CouponWidget - Container Component
 *
 * Reusable widget that displays a horizontal/grid list of coupon cards.
 * Used on Home, PDP, Category pages.
 * Reuses the same bottom sheet (withAppBottomSheet + CouponDetail) used on the cart page
 * for showing coupon details — keeping UI consistent across the app.
 *
 * @see mobile: module-flash-sale/sales-rule/components/coupon-widget-content/
 */

'use client';

import React, { useCallback, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { CouponWidgetView } from './coupon-widget-view';
import type { CouponWidgetProps } from './coupon-widget.types';
import {
    useCouponWidgetContent,
    useAddCouponToWallet,
    useSortedCoupons,
    useShowCouponDetails,
    type AppBottomSheetAction,
    type CouponContentResponse,
} from '@kidslabs/module-flash-sale';
import { withAppBottomSheet, type AppBottomSheetInjectedProps } from '@kidslabs/mario-nextjs-react-ui-core';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'next-i18next';
import { useModalAction } from '@/_application/core/components/ui/modal/modal.context';

interface CouponWidgetInnerProps extends CouponWidgetProps {
    bottomSheet: AppBottomSheetAction;
}

const CouponWidgetInner: React.FC<CouponWidgetInnerProps> = ({
    bottomSheet,
    title,
    filter,
    variant = 'voucher',
    limitItem,
    className,
    isMobile,
}) => {
    const router = useRouter();
    const { t } = useTranslation('flash-sale');
    const { status } = useSession();
    const { openModal } = useModalAction();
    const { couponContent: coupons, loading } = useCouponWidgetContent({ filter });
    const { handleAddCoupon, getActionLabel, isAdding } = useAddCouponToWallet();
    const { sortedCoupons, markAdding } = useSortedCoupons({
        coupons,
        isFocused: true,
        limitItem,
    });

    const bottomSheetProps: AppBottomSheetInjectedProps = useMemo(() => ({ bottomSheet }), [bottomSheet]);

    const showCouponDetails = useShowCouponDetails(bottomSheet, { containerStyle: {} }, t, bottomSheetProps);

    const handleAction = useCallback(
        (coupon: CouponContentResponse) => {
            if (status !== 'authenticated') {
                openModal('LOGIN_VIEW');
                return;
            }
            if (variant === 'eWalletVoucher') {
                showCouponDetails(coupon, variant);
                return;
            }
            markAdding();
            handleAddCoupon(coupon);
        },
        [status, openModal, variant, handleAddCoupon, markAdding, showCouponDetails]
    );

    const handleShowCondition = useCallback(
        (coupon: CouponContentResponse) => {
            showCouponDetails(coupon, variant);
        },
        [showCouponDetails, variant]
    );

    const handleViewAll = useCallback(() => {
        const params = filter ? `?filter=${encodeURIComponent(JSON.stringify(filter))}` : '';
        router.push(`/coupon-list${params}`);
    }, [router, filter]);

    const couponsData = useMemo(
        () =>
            sortedCoupons.map(coupon => ({
                coupon,
                actionLabel: getActionLabel(coupon, variant),
                isAdding: isAdding(coupon.salesrule?.coupon_id?.toString() ?? ''),
                isInWallet: Boolean(coupon.is_in_wallet),
            })),
        [sortedCoupons, getActionLabel, isAdding, variant]
    );

    if (loading && !sortedCoupons.length) {
        return <CouponWidgetView title={title} isLoading className={className} isMobile={isMobile} />;
    }

    if (!sortedCoupons.length) return null;

    return (
        <CouponWidgetView
            title={title}
            coupons={couponsData}
            variant={variant}
            onAction={handleAction}
            onShowCondition={handleShowCondition}
            onViewAll={handleViewAll}
            className={className}
            isMobile={isMobile}
        />
    );
};

export const CouponWidget = withAppBottomSheet<CouponWidgetProps>(
    CouponWidgetInner as React.FC<CouponWidgetProps & { bottomSheet: AppBottomSheetAction }>
);

CouponWidget.displayName = 'CouponWidget';
